/**
 * A contact, on the shared record page.
 *
 * This file is the *configuration* and nothing else: what a contact's mark
 * is, what goes in its meta line, which tabs it has, and what each tab draws.
 * Everything visible — the header, the tabs, the rail, the bottom bar on a
 * phone — belongs to `<RecordPage>` and is identical on a property, a job and
 * a piece of equipment. Rule 1 of the record page plan.
 *
 * What it replaces is `contact-profile.tsx`, a 469-line page that drew its own
 * header, its own tab row, its own two-column grid and its own notes card.
 * Four of those five things are now shared, which is the point: the next
 * module to get a record page writes a config, not a page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom'
import type { MeResponse, NotificationGrid } from '@rowted/shared'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import {
  renderStandardTab, standardTabs, type RelatedTabSpec,
} from '../record/record-standard.js'
import {
  AboutCard, NotificationDots, NotificationLegend, RecordCard, RelatedCard, RelatedRow,
  type AboutField,
} from '../record/record-rail.js'
import {
  isQuietSummary, quietMenuItems, setQuietHidden,
  type QuietConfig, type QuietStep,
} from '../record/record-quiet.js'
import { completeActivity, useRecordSummary, type RecordSummary } from '../lib/use-record.js'
import { NotificationGridEditor } from '../components/notification-grid.js'
import { LinkEditor, type LinkPatch } from '../components/link-editor.js'
import { PortalAccessSlideOver, type PortalAccessRecord } from '../components/portal-access.js'
import { LinkRecord } from '../components/link-record.js'
import { ContactForm } from '../components/forms/contact-form.js'
import { ContactPhoto } from '../components/contact-photo.js'
import { PropertyMap } from '../components/landing-marks.js'
import { EmptyState, humanize } from '../components/primitives.js'
import type { MenuItem } from '../components/context-menu.js'
import type { ProfileJob } from '../lib/record-payloads.js'
import {
  IconCall, IconCompany, IconEdit, IconEmail, IconNote, IconPin, IconPlus, IconSms, IconUpload,
} from '../frame/icons.js'
import { EM_DASH } from '../lib/format.js'
import { request } from '../lib/api.js'
import { mapsLink } from '../lib/maps.js'
import { PlusGlyph } from '../components/keycap.js'
import { openNewRequest } from '../requests/open-request.js'
import { useCanMessage } from '../record/record-message.js'

interface ContactLink {
  id: string
  propertyId: string
  role: string
  isPrimary: boolean
  isBillTo: boolean
  approvesQuotes: boolean
  notificationPrefs: NotificationGrid | null
  effectivePrefs: NotificationGrid
  /** What the editor’s "Back to the contact defaults" resets to. */
  defaultPrefs: NotificationGrid
  usingDefaults: boolean
  accessNote: string | null
  propertyName: string
  addressLine1: string
  city: string
  state: string
  zoneName: string | null
}

interface Contact {
  id: string
  firstName: string
  lastName: string
  title: string
  email: string | null
  phone: string | null
  mobilePhone: string | null
  homePhone: string | null
  officePhone: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  active: boolean
  photoFileId: string | null
  accountId: string | null
  accountName: string | null
  notificationDefaults: NotificationGrid
  preferredChannel: 'email' | 'sms' | 'either' | null
  contactHours: string | null
  version: number
}

interface ContactPayload {
  contact: Contact
  links: ContactLink[]
  jobs: ProfileJob[]
  portalAccess: PortalAccessRecord | null
  resolvedFrom: string | null
}

/** The shape the config closes over — the record, plus what only this page knows. */
export interface ContactRecord extends ContactPayload {
  canEdit: boolean
  orgUnitId: string
  onEdit: () => void
  onPortal: () => void
  onLink: () => void
  onReload: () => void
  onUpload: () => void
  onSaveLink: (linkId: string, patch: LinkPatch) => Promise<void>
  onRemoveLink: (linkId: string) => Promise<void>
  onSaveDefaults: (next: NotificationGrid) => Promise<void>
  /** The quiet panel's sheet if it is up, the Activity tab if it is not. */
  onLogActivity: () => void
  /** Whether this person has hidden the Set up card here — for the ⋯ menu. */
  quietHidden: boolean
  onShowSetup: () => void
}

export function ContactRecord({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<ContactPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [editing, setEditing] = useState(false)
  const [portalOpen, setPortalOpen] = useState(false)
  const [linking, setLinking] = useState(false)
  const [completing, setCompleting] = useState<string | null>(null)

  /*
   * The Files tab lends the page its file picker, so the green quick action
   * under the title can open it — see `<RecordFilesTab onReady>`.
   */
  const openUpload = useRef<(() => void) | null>(null)
  /* The quiet panel's log-activity sheet, lent upwards — see the property
     page for why it is a ref read at click time rather than a flag. */
  const openLog = useRef<(() => void) | null>(null)

  const { summary, reload: reloadSummary } = useRecordSummary('contact', id || undefined)

  const load = useCallback(() => {
    request<ContactPayload>(`/contacts/${id}`)
      .then((payload) => { setData(payload); setError(null) })
      .catch((cause: unknown) => setError(
        cause instanceof Error ? cause.message : 'That contact could not be loaded.'))
  }, [id])

  useEffect(() => { load() }, [load])

  // Who a task can be assigned to. One request for the page, not one per tab.
  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const canEdit = me.permissions.contacts === 'edit' || me.permissions.contacts === 'full'
  /* Whether the Create a job step exists at all — §7, "absent, not greyed". */
  const canCreateJobs = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'
  /* Whether the company bought the portal. A step offering what Rowted has
     not sold them is a step that leads to a 403. */
  const hasPortal = me.features.includes('portal')
  const orgUnitId = me.scope.selected[0] ?? ''

  /** Ticking a box on Next up or Upcoming. */
  const complete = useCallback(async (activityId: string) => {
    setCompleting(activityId)
    try {
      /* One occurrence of a repeating meeting by itself (`completeKey`, 0152). */
      await completeActivity(activityId)
      await reloadSummary()
    } finally {
      setCompleting(null)
    }
  }, [reloadSummary])

  const saveLink = useCallback(async (linkId: string, patch: LinkPatch) => {
    await request(`/contacts/links/${linkId}`, { method: 'PATCH', body: patch })
    load()
  }, [load])

  const removeLink = useCallback(async (linkId: string) => {
    await request(`/contacts/links/${linkId}`, { method: 'DELETE' })
    load()
    void reloadSummary()
  }, [load, reloadSummary])

  const saveDefaults = useCallback(async (next: NotificationGrid) => {
    if (!data) return
    await request(`/contacts/${data.contact.id}`, {
      method: 'PATCH',
      body: { notificationDefaults: next, version: data.contact.version },
    })
    load()
  }, [data, load])

  const record = useMemo<ContactRecord | null>(() => (data ? {
    ...data,
    canEdit,
    orgUnitId,
    onEdit: () => setEditing(true),
    onPortal: () => setPortalOpen(true),
    onLink: () => setLinking(true),
    onReload: () => { load(); void reloadSummary() },
    onUpload: () => openUpload.current?.(),
    onSaveLink: saveLink,
    onRemoveLink: removeLink,
    onSaveDefaults: saveDefaults,
    onLogActivity: () => {
      if (openLog.current) openLog.current()
      else navigate(`/contacts/${id}?tab=activity`)
    },
    quietHidden: summary?.quiet?.hidden ?? false,
    onShowSetup: () => {
      void setQuietHidden('contact', id, false).then(() => reloadSummary())
    },
  } : null), [
    data, canEdit, orgUnitId, id, navigate, load, reloadSummary, saveLink, removeLink,
    saveDefaults, summary?.quiet?.hidden])

  /* Whether *Send a message* is a step at all: only for somebody with an Inbox (Aaron, Oct 24). */
  const canMessage = useCanMessage(id ? { object: 'contact', id } : undefined) !== false

  const config = useMemo(
    () => contactConfig(navigate, { jobs: canCreateJobs, portal: hasPortal, message: canMessage }),
    [navigate, canCreateJobs, hasPortal, canMessage])

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to contacts', onClick: () => navigate('/contacts') }}
      />
    )
  }
  if (!record) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  const { contact, links } = record

  /*
   * The notification grid and portal access are edited on this page but stored
   * on other rows, so the History tab has to be told to include them.
   */
  const related = {
    tables: ['contact_property_links', 'portal_access'],
    recordIds: [
      ...links.map((link) => link.id),
      ...(record.portalAccess ? [record.portalAccess.id] : []),
    ],
  }

  return (
    <>
      {record.resolvedFrom && (
        <div className="border-b border-line bg-info-bg px-4 py-2 text-cell text-info">
          That contact was merged into this one. Links and history came across.
        </div>
      )}

      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => renderStandardTab(tab, {
          object: 'contact',
          id: contact.id,
          href: `/contacts/${contact.id}`,
          summary,
          related: relatedTabs(record, navigate),
          people,
          orgUnitId,
          history: { table: 'contacts', related },
          busyId: completing,
          canEdit,
          onComplete: (activityId) => void complete(activityId),
          onChanged: () => void reloadSummary(),
          onUploadReady: (open) => { openUpload.current = open },
          onLogReady: (open) => { openLog.current = open },
          quiet: config.quiet?.build(record, summary) ?? null,
          onOpen: (path) => navigate(path),
        })}
      </RecordPage>

      <ContactForm
        open={editing}
        onClose={() => setEditing(false)}
        orgUnitId={orgUnitId}
        existing={{ ...contact, version: contact.version }}
        onSaved={() => { setEditing(false); record.onReload() }}
      />

      <LinkRecord
        open={linking}
        onClose={() => setLinking(false)}
        side="contact"
        anchorId={contact.id}
        excludeIds={links.map((link) => link.propertyId)}
        onLinked={record.onReload}
      />

      {portalOpen && (
        <PortalAccessSlideOver
          open={portalOpen}
          onClose={() => setPortalOpen(false)}
          contact={contact}
          access={record.portalAccess}
          properties={links.map((link) => ({
            id: link.id,
            propertyName: link.propertyName,
            role: link.role,
            isBillTo: link.isBillTo,
          }))}
          onSaved={record.onReload}
        />
      )}
    </>
  )
}

/**
 * The three lists a contact has, said once — the tab row and the tab body
 * both read this array, so they cannot disagree.
 *
 * A contact's jobs and visits are its *properties'*, which is why they filter
 * on the linked property ids rather than on the contact. The condition is
 * always there, even with nothing in it: an empty base filter is not "no
 * restriction", it is "no properties", and leaving it out showed this contact
 * every job in the company. `= ANY('{}')` is false for every row.
 */
function relatedTabs(record: ContactRecord, navigate: NavigateFunction): RelatedTabSpec[] {
  const theirs = [{
    field: 'propertyId',
    op: 'in' as const,
    value: record.links.map((link) => link.propertyId),
  }]

  return [
    {
      area: 'jobs',
      listKey: 'jobs@contact',
      baseFilter: theirs,
      // Which property, because a contact's jobs are at several of them.
      fields: ['title', 'status', 'nextAction', 'propertyName', 'expected'],
      emptyBody: 'A job at any of this contact\u2019s properties shows up here.',
      ...(record.canEdit
        ? {
          onNew: {
            label: 'New job',
            onClick: () => navigate(`/jobs/new?contact=${record.contact.id}`),
          },
        }
        : {}),
    },
    {
      area: 'visits',
      listKey: 'visits@contact',
      baseFilter: theirs,
      fields: ['scheduledDate', 'description', 'status', 'propertyName', 'crewName'],
      emptyBody: 'A visit to any of this contact\u2019s properties shows up here.',
      ...(record.canEdit
        ? { onNew: { label: 'Schedule work', onClick: () => navigate('/schedule') } }
        : {}),
    },
    {
      area: 'invoices',
      listKey: 'invoices@contact',
      baseFilter: [{ field: 'billToContactId', op: 'eq', value: record.contact.id }],
      emptyBody: 'An invoice billed to this contact shows up here.',
      ...(record.canEdit
        ? { onNew: { label: 'New invoice', onClick: () => navigate('/invoices/new') } }
        : {}),
    },
  ]
}

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The quiet page
// ---------------------------------------------------------------------------

/**
 * What a contact with no work on them says — `quiet-records.md` §7.
 *
 * A contact is different from a property in one way that shapes the whole
 * list: **a property is a place and a contact is a person**, so the first
 * step is not "make a job" but "which place are they about". Without a
 * property linked, a job on this contact has nowhere to happen — so
 * `property` leads, and once it is linked it sinks to the end wearing the
 * property's name, which is the thing somebody opening the record wanted to
 * know anyway.
 *
 * **Send a message** is the one step in the product that does not go
 * anywhere: it aims the composer that is already on the page (`composerKind`)
 * at Text or Email. Texting somebody is not a screen.
 */
export function contactQuiet(
  record: ContactRecord,
  /** `message`: an Inbox — without one there is no Email or Text to aim at (Aaron, Oct 24). */
  can: { jobs: boolean; portal: boolean; message?: boolean },
  navigate: NavigateFunction,
  /** What is connected to them — the file count is only here. */
  summary: RecordSummary | null,
): QuietConfig {
  const { contact, links, canEdit } = record
  const files = summary?.counts.files ?? 0
  const first = contact.firstName || 'this contact'
  const linked = links[0]
  const mobile = contact.mobilePhone

  /* The three About fields a contact is most often missing. */
  const blanks = [
    ...(contact.officePhone ? [] : ['office']),
    ...(contact.homePhone ? [] : ['home']),
    ...(contact.addressLine1 ? [] : ['address']),
  ]

  const portalOn = Boolean(record.portalAccess && !record.portalAccess.revokedAt)

  const steps: QuietStep[] = [
    {
      key: 'property',
      icon: 'properties',
      title: 'Link a property',
      body: `Where ${first} wants work done.`,
      state: !canEdit ? 'absent' : linked ? 'done' : 'todo',
      doneBody: links.length === 1
        ? linked?.propertyName ?? ''
        : `${linked?.propertyName ?? ''} and ${links.length - 1} more`,
      onSelect: record.onLink,
    },
    {
      key: 'job',
      icon: 'jobs',
      title: 'Create a job',
      body: `Start a quote for ${first}.`,
      /* Done once there is a job — board 5's busy cell, and the walkthrough's
         *Create a job* offered on a contact with one (Oct 30, §8). */
      state: !can.jobs ? 'absent' : (summary?.counts.jobs ?? 0) > 0 ? 'done' : 'todo',
      onSelect: () => navigate(`/jobs/new?contact=${contact.id}`),
    },
    {
      key: 'message',
      icon: mobile ? 'message' : 'email',
      title: 'Send a message',
      body: `Text or email ${first} — it is logged here.`,
      /* Nothing to send to is nothing to offer, and nor is nothing to send with — no Inbox. */
      state: !canEdit || can.message === false || (!mobile && !contact.phone && !contact.email) ? 'absent' : 'todo',
      /* The composer, not a screen — see the note above. Text where there is
         a mobile, because that is the one somebody reaches for first. */
      composerKind: mobile ? 'sms' : 'email',
    },
    {
      key: 'portal',
      /* A shield, the same glyph the organization's Access tab wears: who
         may get in is the question both of them ask. */
      icon: 'access',
      title: 'Give portal access',
      body: `Let ${first} approve quotes and pay online.`,
      /* Absent without the feature or without an address to send it to: the
         portal invitation is an email, so a contact with no email has no way
         in at all (§1 rule 4). */
      state: !canEdit || !can.portal || !contact.email
        ? 'absent'
        : portalOn ? 'done' : 'todo',
      doneBody: 'They can approve quotes and pay online.',
      onSelect: record.onPortal,
    },
    {
      key: 'details',
      icon: 'details',
      title: 'Finish the details',
      body: blanks.length === 0
        ? 'Everything About asks for is filled in.'
        : `${upperFirst(sentenceList(blanks))} ${blanks.length === 1 ? 'is' : 'are'} blank.`,
      state: !canEdit ? 'absent' : blanks.length === 0 ? 'done' : 'todo',
      doneBody: 'Numbers, address and preferences are all set.',
      onSelect: () => navigate(`/contacts/${contact.id}?focus=about`),
    },
    {
      key: 'files',
      icon: 'files',
      kind: 'files',
      title: 'Attach files',
      body: 'Drop a W-9, a contract or an ID anywhere here.',
      phone: { title: 'Add photos or files', body: 'A W-9, a contract or an ID.' },
      state: !canEdit ? 'absent' : files > 0 ? 'done' : 'todo',
      doneBody: files === 1 ? '1 file' : `${files} files`,
    },
  ]

  return {
    headline: `No work with ${first} yet`,
    sub: linked
      ? `${first} is linked to ${linked.propertyName}, but nothing has happened.`
        + ' Log a call, start a job or send a message.'
      : `Nothing has happened with ${first} yet. Link a property, then start a job.`,
    composerSubject: first,
    setupSubject: first,
    steps,
    ghosts: [
      { title: 'Next up', line: `Calls, tasks and meetings about ${first}.`, icon: 'clock' },
      { title: 'Jobs', line: `Quotes and work ${first} has asked for.`, icon: 'jobs' },
      { title: 'Notes', line: 'What was said, and who said it.', icon: 'note' },
    ],
    canWrite: canEdit,
  }
}

/** "office", "office and home", "office, home and address". */
function sentenceList(words: string[]): string {
  if (words.length <= 1) return words[0] ?? ''
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`
}

/** The sentence starts with a capital, whichever field happens to be first. */
const upperFirst = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

function contactConfig(
  navigate: NavigateFunction,
  /** What this viewer and this company allow — permissions, an Inbox and a sold feature. */
  can: { jobs: boolean; portal: boolean; message: boolean },
): RecordConfig<ContactRecord> {
  const nameOf = (record: ContactRecord) =>
    `${record.contact.firstName} ${record.contact.lastName}`.trim()

  return {
    object: 'contact',

    breadcrumb: (record) => [
      { label: 'Contacts', href: '/contacts' },
      { label: nameOf(record) },
    ],

    mark: (record) => (
      <ContactPhoto
        contactId={record.contact.id}
        name={nameOf(record)}
        photoFileId={record.contact.photoFileId}
        canEdit={record.canEdit}
        orgUnitId={record.orgUnitId}
        onChanged={record.onReload}
      />
    ),

    title: nameOf,

    /*
     * Tags say what is *true* about this contact. The company is not a tag —
     * it is in the meta line below, and a name that appeared twice in two
     * shapes read as two different companies.
     */
    tags: (record) => [
      ...(record.links.some((link) => link.approvesQuotes) ? [{ label: 'Approves quotes' }] : []),
      { label: record.portalAccess && !record.portalAccess.revokedAt ? 'Portal on' : 'Portal off' },
      ...(!record.contact.active ? [{ label: 'Deactivated' }] : []),
    ],

    /*
     * The meta line, in the order somebody reads it: what they do, how to
     * reach them, who they are with. It loses items from the right as the
     * window narrows — which is why the phone number is second and the
     * company is last.
     */
    meta: (record) => {
      const { contact } = record
      const phone = contact.mobilePhone ?? contact.phone
      const who = [contact.title, contact.accountName].filter(Boolean).join(' · ')
      return [
        ...(who ? [{ icon: <IconCompany size={13} />, text: who }] : []),
        ...(phone ? [{ icon: <IconCall size={13} />, text: phone, href: `tel:${phone}` }] : []),
        ...(contact.email
          ? [{ icon: <IconEmail size={13} />, text: contact.email, href: `mailto:${contact.email}` }]
          : []),
        ...(contact.preferredChannel
          ? [{
            text: `Prefers ${contact.preferredChannel === 'sms' ? 'text' : contact.preferredChannel}`
              + (contact.contactHours ? ` · ${contact.contactHours}` : ''),
          }]
          : []),
      ]
    },

    primary: (record) => (record.canEdit
      ? {
        label: 'New job',
        icon: <IconPlus size={14} />,
        onClick: () => navigate(`/jobs/new?contact=${record.contact.id}`),
      }
      : null),

    actions: (record) => (record.canEdit
      ? [
        { label: 'Edit', icon: <IconEdit size={14} />, onClick: record.onEdit },
        { label: 'Portal access', onClick: record.onPortal },
      ]
      : []),

    menu: (record) => {
      const items: MenuItem[] = []
      /* First, for whoever may take one (`new-request.md` §13.3, Board 1 H). */
      if (can.jobs) {
        const first = record.contact.firstName || nameOf(record)
        items.push({
          key: 'new-request',
          label: `New request for ${first}`,
          sub: `${first} filled in, ${first}’s properties offered`,
          icon: <PlusGlyph size={15} stroke={2.2} />,
          keys: ['N', 'R'],
          onSelect: () => openNewRequest({ contactId: record.contact.id }),
        })
      }
      if (record.canEdit) {
        items.push({ key: 'link', label: 'Link a property', onSelect: record.onLink })
      }
      items.push(...quietMenuItems(record.quietHidden, record.onShowSetup))
      return items
    },

    /*
     * Tabs a contact does not use do not appear. A contact with no invoices
     * gets no Invoices tab, rather than a tab holding an empty state — §3.3:
     * "a tab with nothing in it is a tab somebody opens once".
     */
    tabs: (record, summary) => standardTabs({
      counts: summary?.counts ?? {},
      related: relatedTabs(record, navigate),
    }),

    rail: (record) => <ContactRail record={record} />,

    quiet: {
      when: (record, summary) => isQuietSummary(summary, relatedTabs(record, navigate)),
      build: (record, summary) => contactQuiet(record, can, navigate, summary),
    },

    phone: {
      /*
       * Call, Text, Email — the three things somebody standing outside a
       * customer's house does, as `tel:`/`sms:`/`mailto:` links so the phone's
       * own app takes over. A contact with no mobile gets their office number
       * on Call and no Text at all, rather than a button that opens an empty
       * message.
       */
      quickActions: (record) => {
        const { contact } = record
        const phone = contact.mobilePhone ?? contact.phone ?? contact.officePhone
        return [
          ...(phone ? [{ label: 'Call', icon: <IconCall size={18} />, href: `tel:${phone}` }] : []),
          ...(contact.mobilePhone
            ? [{ label: 'Text', icon: <IconSms size={18} />, href: `sms:${contact.mobilePhone}` }]
            : []),
          ...(contact.email
            ? [{ label: 'Email', icon: <IconEmail size={18} />, href: `mailto:${contact.email}` }]
            : []),
        ]
      },
      tabBar: ['overview', 'activity', 'jobs', 'invoices'],
      /*
       * The green one is about the tab you are on (§8). On Activity there is
       * no green button at all: the composer is the first thing on the tab,
       * and a button that scrolled you to something already on screen is a
       * button that wastes a quarter of the row.
       */
      primaryByTab: (tab, record) => {
        if (!record.canEdit || tab === 'activity') return null
        if (tab === 'files') {
          return { label: 'Upload', icon: <IconUpload size={18} />, onClick: record.onUpload }
        }
        if (tab === 'overview') {
          return {
            label: 'Log activity',
            icon: <IconNote size={18} />,
            /* The panel's own sheet while it is up — the record decides. */
            onClick: record.onLogActivity,
          }
        }
        return {
          label: 'New job',
          icon: <IconPin size={18} />,
          onClick: () => navigate(`/jobs/new?contact=${record.contact.id}`),
        }
      },
    },
  }
}

// ---------------------------------------------------------------------------
// The two pieces that are a contact's own
// ---------------------------------------------------------------------------

function ContactRail({ record }: { record: ContactRecord }) {
  const navigate = useNavigate()
  const { contact, links } = record
  const [editingLink, setEditingLink] = useState<ContactLink | null>(null)

  const address = [
    contact.addressLine1,
    contact.addressLine2,
    [contact.city, contact.state].filter(Boolean).join(', '),
    contact.postalCode,
  ].filter(Boolean).join(' · ')

  const maps = mapsLink([
    contact.addressLine1, contact.addressLine2, contact.city, contact.state, contact.postalCode,
  ])

  const fields: AboutField[] = [
    { label: 'Title', value: contact.title || EM_DASH },
    { label: 'Company', value: contact.accountName ?? EM_DASH },
    {
      label: 'Mobile',
      value: contact.mobilePhone ?? EM_DASH,
      ...(contact.mobilePhone ? { href: `tel:${contact.mobilePhone}` } : {}),
    },
    { label: 'Office', value: contact.officePhone ?? EM_DASH },
    { label: 'Home', value: contact.homePhone ?? EM_DASH },
    {
      label: 'Email',
      value: contact.email ?? EM_DASH,
      ...(contact.email ? { href: `mailto:${contact.email}` } : {}),
    },
    {
      label: 'Address',
      value: address || EM_DASH,
      ...(maps && address ? { href: maps.href } : {}),
    },
    {
      label: 'Prefers',
      value: contact.preferredChannel
        ? [
          contact.preferredChannel === 'sms' ? 'Text' : humanize(contact.preferredChannel),
          contact.contactHours,
        ].filter(Boolean).join(' · ')
        : EM_DASH,
    },
  ]

  return (
    <>
      {/* `?focus=about` is where "Finish the details" lands (§7). */}
      <AboutCard
        fields={fields}
        onEdit={record.canEdit ? record.onEdit : undefined}
        focusKey="about"
        {...(record.canEdit ? { onFocus: record.onEdit } : {})}
      />

      <RelatedCard
        title="Properties"
        count={links.length}
        addLabel={record.canEdit ? 'Link' : undefined}
        onAdd={record.canEdit ? record.onLink : undefined}
        legend={links.length > 0 ? <NotificationLegend /> : undefined}
      >
        {links.length === 0
          ? (
            <p className="m-0 px-[18px] py-3 text-body text-text-muted">
              Not on a property yet.
            </p>
          )
          : links.map((link) => (
            <RelatedRow
              key={link.id}
              /*
                * The property's own map square, the same one the Properties
                * list draws. A generic pin on every row told you they were
                * properties, which you already knew from the card's title;
                * the map is different per property, so the eye finds the one
                * it saw on the list.
                */
              mark={(
                <PropertyMap
                  seed={link.propertyId}
                  colour={null}
                  size={34}
                  radius={8}
                  title={link.zoneName ?? 'No zone'}
                />
              )}
              title={link.propertyName}
              sub={(
                <>
                  {link.role}
                  {link.isPrimary && ' · primary'}
                  {link.isBillTo && ' · bill to'}
                  {link.approvesQuotes && ' · approves quotes'}
                </>
              )}
              dots={(
                <NotificationDots
                  grid={link.effectivePrefs as unknown as
                    Record<string, Record<string, boolean | undefined>>}
                  onOpen={() => setEditingLink(link)}
                />
              )}
              /*
                * The row opens the **property**, and the dots open the link.
                *
                * It opened the link from anywhere, on the argument that a
                * click on this card is almost always about the relationship.
                * In use that is the wrong way round: a row that names a place
                * and shows its photo reads as a link to that place, and
                * getting an edit dialog instead is a click nobody asked for.
                * The relationship still has a target — the oval of dots, the
                * one part of the row that is about the link rather than the
                * record — and Remove and the rest are inside it.
                */
              onOpen={() => navigate(`/properties/${link.propertyId}`)}
            />
          ))}
      </RelatedCard>

      {/*
        * The contact's own defaults, in the rail rather than a tab of their
        * own. A tab called "Notification defaults" is a tab nobody opens
        * looking for a contact, and it was the only one on the page that did
        * not hold a list of something.
        */}
      <RecordCard title="Notification defaults">
        <div className="px-[18px] pb-4">
          <NotificationGridEditor
            label="Applied to new properties"
            grid={contact.notificationDefaults}
            readOnly={!record.canEdit}
            onChange={record.canEdit ? (next) => void record.onSaveDefaults(next) : undefined}
          />
          <p className="m-0 mt-3 text-[11px] leading-[1.45] text-text-faint">
            Per-property overrides win over these. Changing them does not change a property
            where somebody has already set the grid by hand.
          </p>
        </div>
      </RecordCard>

      {editingLink && (
        <LinkEditor
          link={editingLink}
          title={editingLink.propertyName}
          subtitle={[editingLink.addressLine1, editingLink.city].filter(Boolean).join(', ')}
          canEdit={record.canEdit}
          onClose={() => setEditingLink(null)}
          onSave={(patch) => record.onSaveLink(editingLink.id, patch)}
          onRemove={record.canEdit
            ? () => record.onRemoveLink(editingLink.id)
            : undefined}
          onOpenRecord={() => navigate(`/properties/${editingLink.propertyId}`)}
          openRecordLabel="Open property"
        />
      )}
    </>
  )
}
